/**
 * 키 하나 → 의도 하나 (v10, `docs/25` §3.1).
 *
 * **순수 함수로 뗀다** — 이 패키지는 node 환경에서 테스트한다(jsdom 미도입). `KeyboardEvent`
 * 를 직접 받으면 이 판정이 검사에서 통째로 빠진다 (`routing.ts`·`client-type.ts`·
 * `channel-selection.ts` 와 같은 이유). 값을 읽는 일은 어댑터(`use-tv-focus.ts`) 한 줄이 맡고,
 * **`eslint.config.mjs` 의 `no-restricted-globals` 가 이 폴더에 DOM 을 들이지 못하게 한다.**
 *
 * ── Enter 를 「활성화」로 만들지 않는다 ────────────────────────
 *
 * 여기서 Enter 는 `confirm` 이고, 그것이 뜻하는 것은 **「눌렀다」가 아니라 「눌렀는지
 * 판단해야 한다」**다. 실제 활성화는 네이티브 `<button>` 이 한다 (절대 규칙 30) —
 * 우리가 `click()` 을 부르면 같은 동작이 두 번 일어나 `purchase_click` 이 두 건이 되고,
 * 그 증상은 화면이 아니라 분석에서만 갈린다.
 *
 * `confirm` 이 존재하는 이유는 하나다. **층 밖에 포커스가 있을 때 그것을 삼켜야 한다**
 * (절대 규칙 32) — 시트가 열려도 `document.activeElement` 는 시트를 연 버튼에 남아 있고,
 * 삼키지 않으면 시트 위에서 Enter 를 눌렀을 때 뒤의 `구매하기` 가 다시 눌린다.
 * 그 판단은 `decide.ts` 가 한다.
 */

/** 방향 넷. 대각선은 없다 — 리모컨 십자키에 없다. */
export type Direction = 'up' | 'down' | 'left' | 'right';

export type NavIntent =
  | { kind: 'move'; direction: Direction }
  /** Enter·OK·Space. **활성화가 아니다** — 위 주석 */
  | { kind: 'confirm' }
  /** 뒤로. 열린 층 하나를 닫는다 */
  | { kind: 'back' };

/**
 * 키 이벤트에서 판정에 필요한 것만 뽑은 값.
 *
 * **`KeyboardEvent` 가 아니다.** node 에서 손으로 만들 수 있어야 이 판정 전체가 검사에
 * 덮인다 (`geometry.ts` 의 `Rect` 가 `DOMRect` 가 아닌 것과 같은 이유).
 */
export interface KeySignals {
  /** `event.key` */
  readonly key: string;
  /** `event.keyCode`. **구형 TV 브라우저는 이것만 준다** — 아래 `LEGACY_CODES` */
  readonly keyCode: number;
  /** `event.repeat` — 길게 누르고 있는가 */
  readonly repeat: boolean;
  readonly alt: boolean;
  readonly ctrl: boolean;
  readonly meta: boolean;
  readonly shift: boolean;
  /** 이벤트 대상이 글자를 받는 자리인가 (`input`·`textarea`·`contenteditable`) */
  readonly editable: boolean;
}

/**
 * `event.key` 로 오는 이름들.
 *
 * 레거시 표기(`Left`·`Up`·`Esc`·`Spacebar`)를 함께 받는다 — 표준 이름이 정해지기 전의
 * TV 브라우저가 그렇게 보낸다. 받아서 잃을 것이 없고, 빠뜨리면 그 기기에서 **십자키
 * 전체가 죽는다.**
 */
const KEY_NAMES: Readonly<Record<string, NavIntent>> = {
  ArrowUp: { kind: 'move', direction: 'up' },
  ArrowDown: { kind: 'move', direction: 'down' },
  ArrowLeft: { kind: 'move', direction: 'left' },
  ArrowRight: { kind: 'move', direction: 'right' },
  Up: { kind: 'move', direction: 'up' },
  Down: { kind: 'move', direction: 'down' },
  Left: { kind: 'move', direction: 'left' },
  Right: { kind: 'move', direction: 'right' },
  Enter: { kind: 'confirm' },
  NumpadEnter: { kind: 'confirm' },
  ' ': { kind: 'confirm' },
  Spacebar: { kind: 'confirm' },
  Escape: { kind: 'back' },
  Esc: { kind: 'back' },
  /** Tizen·webOS 가 뒤로 키에 붙이는 이름 */
  XF86Back: { kind: 'back' },
  BrowserBack: { kind: 'back' },
  GoBack: { kind: 'back' },
};

/**
 * `event.keyCode` 폴백. **`key` 가 이름을 주지 않을 때만 본다.**
 *
 * 구형 Tizen·webOS 는 십자키와 OK 를 `key: 'Unidentified'` 로 보내고 코드만 채운다.
 * 이것이 없으면 그 기기에서 리모컨이 통째로 죽는데, **개발 기기에서는 영원히 드러나지
 * 않는다** — 여기가 그 폴백이 사는 유일한 자리다.
 *
 * `10009`(Tizen) · `461`(webOS) 은 각 플랫폼의 뒤로 키다.
 */
const LEGACY_CODES: Readonly<Record<number, NavIntent>> = {
  13: { kind: 'confirm' },
  27: { kind: 'back' },
  32: { kind: 'confirm' },
  37: { kind: 'move', direction: 'left' },
  38: { kind: 'move', direction: 'up' },
  39: { kind: 'move', direction: 'right' },
  40: { kind: 'move', direction: 'down' },
  461: { kind: 'back' },
  10009: { kind: 'back' },
};

/** `key` 가 이름을 주지 않는 경우. 이때만 `keyCode` 로 내려간다. */
function hasNoKeyName(key: string): boolean {
  return key === '' || key === 'Unidentified';
}

/**
 * 이 키가 무엇을 뜻하는가. **모르는 키는 `null` 이고, `null` 은 「손대지 않는다」다.**
 *
 * ── 손대지 않는 것들과 그 이유 ────────────────────────────────
 *
 * - **수식키가 하나라도 켜져 있으면** — `Alt+←` 는 브라우저 뒤로가기다. 우리가 삼키면
 *   그 조작이 죽고, 증상은 「이 앱에서만 뒤로가기가 안 된다」다.
 * - **`editable`** — 지금 이 앱에 `input`·`textarea`·`contenteditable` 이 **하나도 없어서
 *   증상이 없다. 그래서 조용하다.** 훗날 검색창이 생기는 날 화살표로 커서를 못 움직인다.
 * - **`repeat` 인 `confirm`·`back`** — 길게 누르면 한 번 눌렀는데 **시트 두 층이 닫힌다.**
 *   이동은 반대다: 길게 눌러 옮겨 가는 것이 리모컨의 정상 조작이라 그대로 통과시킨다.
 * - `Tab` — **가로채지 않는다.** 네이티브 탭 순서가 살아 있어야 포커스 링에서 뺀 것들
 *   (GIS 로그인 iframe·링크)에 닿는 탈출구가 된다 (`docs/25` §4).
 *
 * Space 를 `confirm` 으로 두는 것은 **비용이 없다.** 층 안에서는 `decide.ts` 가
 * `preventDefault` 하지 않으므로 네이티브 동작(버튼 활성화·스크롤)이 그대로이고,
 * 층 밖에서만 삼켜진다 — Enter 와 같은 위험(시트 뒤 버튼이 눌린다)을 함께 막는다.
 */
export function resolveNavIntent(signals: KeySignals): NavIntent | null {
  if (signals.alt || signals.ctrl || signals.meta || signals.shift) return null;
  if (signals.editable) return null;

  const intent = hasNoKeyName(signals.key) ? LEGACY_CODES[signals.keyCode] : KEY_NAMES[signals.key];
  if (intent === undefined) return null;

  if (signals.repeat && intent.kind !== 'move') return null;
  return intent;
}
